import React, { useEffect, useState } from 'react';

interface LogEntry {
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
}

// Константа для админского chat_id
const ADMIN_CHAT_ID = 1008837582;

const GlobalErrorLogger: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Проверяем chat_id пользователя из Telegram WebApp
    const checkAdminAccess = () => {
      try {
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.initDataUnsafe?.user?.id) {
          const chatId = tg.initDataUnsafe.user.id;
          console.log('[GlobalErrorLogger] Проверка доступа для chat_id:', chatId);
          
          if (chatId === ADMIN_CHAT_ID) {
            setIsAdmin(true);
            console.log('[GlobalErrorLogger] ✓ Админский доступ предоставлен');
          } else {
            console.log('[GlobalErrorLogger] ✗ Консоль разработчика доступна только админу');
          }
        } else {
          // В режиме разработки (вне Telegram) или если данные недоступны
          // Проверяем URL параметр для тестирования
          const urlParams = new URLSearchParams(window.location.search);
          const debugMode = urlParams.get('debug') === 'true';
          
          if (debugMode || process.env.NODE_ENV === 'development') {
            setIsAdmin(true);
            console.log('[GlobalErrorLogger] ✓ Debug режим включен');
          }
        }
      } catch (error) {
        console.error('[GlobalErrorLogger] Ошибка при проверке доступа:', error);
      } finally {
        setIsChecking(false);
      }
    };

    // Даем время на загрузку Telegram WebApp скрипта
    setTimeout(checkAdminAccess, 100);
  }, []);

  useEffect(() => {
    // Не устанавливаем обработчики, если это не админ
    if (!isAdmin) {
      return;
    }

    // Capture global errors
    const handleError = (event: ErrorEvent) => {
      const log: LogEntry = {
        timestamp: new Date().toISOString(),
        type: 'error',
        message: event.message || 'Unknown error',
        stack: event.error?.stack,
      };
      
      console.error('Global error:', event);
      setLogs(prev => [...prev, log]);
      setHasErrors(true);
      setIsVisible(true); // Автоматически показываем панель при ошибке
    };

    // Capture unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const log: LogEntry = {
        timestamp: new Date().toISOString(),
        type: 'error',
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
      };
      
      console.error('Unhandled rejection:', event);
      setLogs(prev => [...prev, log]);
      setHasErrors(true);
      setIsVisible(true);
    };

    // Intercept console.error
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      const log: LogEntry = {
        timestamp: new Date().toISOString(),
        type: 'error',
        message: args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' '),
      };
      
      setLogs(prev => [...prev, log]);
      setHasErrors(true);
      originalConsoleError.apply(console, args);
    };

    // Intercept console.warn
    const originalConsoleWarn = console.warn;
    console.warn = (...args: any[]) => {
      const log: LogEntry = {
        timestamp: new Date().toISOString(),
        type: 'warning',
        message: args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' '),
      };
      
      setLogs(prev => [...prev, log]);
      originalConsoleWarn.apply(console, args);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Добавим информационное сообщение о старте
    setLogs([{
      timestamp: new Date().toISOString(),
      type: 'info',
      message: '✓ Приложение запускается...',
    }]);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, [isAdmin]);

  const clearLogs = () => {
    setLogs([{
      timestamp: new Date().toISOString(),
      type: 'info',
      message: '✓ Логи очищены',
    }]);
    setHasErrors(false);
  };

  const copyLogs = () => {
    const logsText = logs.map(log => 
      `[${new Date(log.timestamp).toLocaleString('ru-RU')}] ${log.type.toUpperCase()}: ${log.message}${log.stack ? '\n' + log.stack : ''}`
    ).join('\n\n');

    navigator.clipboard.writeText(logsText).then(() => {
      alert('Логи скопированы в буфер обмена');
    }).catch(() => {
      alert('Не удалось скопировать логи');
    });
  };

  // Не показываем компонент, если пользователь не админ
  if (!isAdmin) {
    return null;
  }

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: hasErrors ? '#dc2626' : '#3b82f6',
          color: '#fff',
          border: 'none',
          borderRadius: '50%',
          width: '56px',
          height: '56px',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: hasErrors ? 'pulse 2s infinite' : 'none',
        }}
        title="Показать логи (только для админа)"
      >
        {hasErrors ? '⚠️' : '📋'}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      maxHeight: '50vh',
      backgroundColor: '#1a1a1a',
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: '12px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      borderTop: hasErrors ? '3px solid #dc2626' : '3px solid #3b82f6',
      boxShadow: '0 -4px 12px rgba(0,0,0,0.5)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: '#2d2d2d',
        borderBottom: '1px solid #404040',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '18px' }}>
            {hasErrors ? '⚠️' : '📋'}
          </span>
          <strong>
            Консоль разработчика {hasErrors && '(ОШИБКИ!)'}
          </strong>
          <span style={{
            backgroundColor: '#8b5cf6',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: 'bold',
          }}>
            ADMIN
          </span>
          <span style={{
            backgroundColor: hasErrors ? '#dc2626' : '#6b7280',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
          }}>
            {logs.length} записей
          </span>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={copyLogs}
            style={{
              backgroundColor: 'transparent',
              color: '#9ca3af',
              border: '1px solid #404040',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            📋 Копировать
          </button>
          <button
            onClick={clearLogs}
            style={{
              backgroundColor: 'transparent',
              color: '#9ca3af',
              border: '1px solid #404040',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            🗑️ Очистить
          </button>
          <button
            onClick={() => setIsVisible(false)}
            style={{
              backgroundColor: 'transparent',
              color: '#9ca3af',
              border: '1px solid #404040',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            ✕ Закрыть
          </button>
        </div>
      </div>

      {/* Logs List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px',
      }}>
        {logs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#6b7280',
            padding: '20px',
          }}>
            Логов пока нет
          </div>
        ) : (
          logs.map((log, index) => (
            <div
              key={index}
              style={{
                marginBottom: '8px',
                padding: '8px 12px',
                backgroundColor: 
                  log.type === 'error' ? '#2d1a1a' : 
                  log.type === 'warning' ? '#2d2a1a' : 
                  '#1a2a2d',
                borderLeft: `4px solid ${
                  log.type === 'error' ? '#dc2626' : 
                  log.type === 'warning' ? '#fbbf24' : 
                  '#3b82f6'
                }`,
                borderRadius: '4px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
              }}>
                <span style={{ fontSize: '14px' }}>
                  {log.type === 'error' ? '❌' : 
                   log.type === 'warning' ? '⚠️' : 
                   'ℹ️'}
                </span>
                <span style={{
                  fontSize: '10px',
                  color: '#9ca3af',
                }}>
                  {new Date(log.timestamp).toLocaleTimeString('ru-RU')}
                </span>
                <span style={{
                  fontSize: '10px',
                  color: log.type === 'error' ? '#fca5a5' : 
                         log.type === 'warning' ? '#fde68a' : 
                         '#93c5fd',
                  textTransform: 'uppercase',
                }}>
                  {log.type}
                </span>
              </div>
              <pre style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '12px',
                lineHeight: '1.4',
                color: log.type === 'error' ? '#fca5a5' : 
                       log.type === 'warning' ? '#fde68a' : 
                       '#e5e7eb',
              }}>
                {log.message}
              </pre>
              {log.stack && (
                <details style={{ marginTop: '4px' }}>
                  <summary style={{
                    cursor: 'pointer',
                    color: '#9ca3af',
                    fontSize: '11px',
                  }}>
                    Stack trace
                  </summary>
                  <pre style={{
                    margin: '4px 0 0 0',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '10px',
                    lineHeight: '1.4',
                    color: '#6b7280',
                  }}>
                    {log.stack}
                  </pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GlobalErrorLogger;

